// src/frontend/js/modern/core/sync-gateway.js
import { stateCache } from './state-cache.js';
import { displayStore } from './storage/display-store.js';
import { eventBus, EVENTS } from './event-bus.js';
import {extractListsFromObject, normalizeItems} from "../shared/store-helpers";

const LIST_SPECIFIC_FIELDS = [
	'remove_url', 'remove_nonce', 'wishlist_token',
	'list_name', 'item_id', 'wishlist_id'
];

function extractDisplays(moduleName, items) {
	if (!Array.isArray(items)) return;
	for (const item of items) {
		if (item?.display && item.product_id !== undefined) {
			displayStore.set(moduleName, item.product_id, item.display);
			// Promote list-specific fields from display to the item itself
			// before stripping the display object.
			for (const key of LIST_SPECIFIC_FIELDS) {
				if (item.display[key] !== undefined && item[key] === undefined) {
					item[key] = item.display[key];
				}
			}
			delete item.display;
		}
	}
}

function extractMultiListDisplays(listsData) {
	const lists = extractListsFromObject(listsData);
	for (const list of lists) {
		const rawItems = list?.wishlist_items || list?.items || {};
		const items = normalizeItems(rawItems);
		for (const item of items) {
			if (item?.display && item.product_id !== undefined) {
				displayStore.set('multiList', item.product_id, item.display);
				for (const key of LIST_SPECIFIC_FIELDS) {
					if (item.display[key] !== undefined && item[key] === undefined) {
						item[key] = item.display[key];
					}
				}
				delete item.display;
			}
		}
	}
}

class SyncGateway {
	applyServerData(data, options = {}) {
		const isMerge = window.wlfmc_l10n?.merge_lists === 'true';
		if (data.wishlist) {
			const items = normalizeItems(data.wishlist.items);
			extractDisplays('wishlist', items);
			if (isMerge && data.lists) {
				const lists = extractListsFromObject(data.lists);
				for (const list of lists) {
					const listItems = normalizeItems(list.items || list.wishlist_items);
					for (const item of listItems) {
						if (item?.display && item.product_id !== undefined) {
							displayStore.set('wishlist', item.product_id, item.display);
						}
					}
				}
			}
			stateCache.setModuleData('wishlist', items, data.wishlist.count || items.length);
		}
		if (data.waitlist) {
			const items = normalizeItems(data.waitlist.items);
			extractDisplays('waitlist', items);
			stateCache.setModuleData('waitlist', items, data.waitlist.count || items.length);
		}
		if (data.lists) {
			extractMultiListDisplays(data.lists);
			const lists = extractListsFromObject(data.lists).map(list => ({
				...list,
				wishlist_items: normalizeItems(list.wishlist_items || list.items),
			}));
			stateCache.setModuleData('multiList', lists, data.lists.count || lists.length);
		}
		eventBus.emit(EVENTS.SYNC_COMPLETED, {
			data,
			remoteTriggered: !!options.remoteTriggered,
			source: 'gateway',
		});
	}

	applyHydrationData(module, items, count) {
		if (module === 'wishlist' || module === 'waitlist') {
			extractDisplays(module, items);
		} else if (module === 'multiList') {
			extractMultiListDisplays({ lists: items });
			items = items.map(list => ({
				...list,
				wishlist_items: normalizeItems(list.wishlist_items || list.items),
			}));
		}
		stateCache.setModuleData(module, items, count ?? items.length);
	}

	patchItem(module, id, updates) {
		stateCache.updateItem(module, id, updates);
	}

	upsertItem(module, id, data) {
		stateCache.upsertItem(module, id, data);
	}

	removeItem(module, id) {
		stateCache.removeItem(module, id);
	}
}

export const syncGateway = new SyncGateway();
export default syncGateway;
